// 쇼 미 더 코인 (Show Me The Coin) engine types.
// Rules: docs/wireframes/show-me-the-coin/rule.md (2026-06-24 design-confirmed).
// Commit-reveal + poker-lite betting. Each round: lead submits 2-6 coins (count
// public, value hidden), follower submits within ±1, both ante + bet (1-chip
// unit, 1-raise cap, check/bet/call/raise/fold), then the higher total COIN
// VALUE wins the pot (tie = push). Coins are consumed. First to 0 chips loses.
// Hidden information: a player's coin inventory and the per-denomination
// breakdown of their current submission are private (opponent sees count only).

export const SHOW_ME_THE_COIN_PLAYERS = ["p1", "p2"] as const;
/** Coin denominations, high → low. */
export const COIN_DENOMINATIONS = [500, 100, 50, 10] as const;

export type ShowMeTheCoinPlayerId = (typeof SHOW_ME_THE_COIN_PLAYERS)[number];
export type CoinDenomination = (typeof COIN_DENOMINATIONS)[number];

/** Starting coin inventory per player (50 coins, total value 3000). */
export const INITIAL_COIN_INVENTORY: Readonly<
  Record<CoinDenomination, number>
> = {
  500: 3,
  100: 7,
  50: 10,
  10: 30,
};

// Ruleset flags (parameterized — see rule.md §5).
export const SHOW_ME_THE_COIN_START_CHIPS = 10;
export const SHOW_ME_THE_COIN_ANTE = 1;
export const SHOW_ME_THE_COIN_BET_UNIT = 1;
export const SHOW_ME_THE_COIN_RAISE_CAP = 1;
export const LEAD_MIN_SUBMIT = 2;
export const LEAD_MAX_SUBMIT = 6;
/** Safety cap on rounds for non-terminating play (sims, malformed bots). */
export const SHOW_ME_THE_COIN_ROUND_CAP = 100;

export type ShowMeTheCoinPhase =
  | "submit-lead"
  | "submit-follow"
  | "bet"
  | "game-over";

export type BetMove = "check" | "bet" | "call" | "raise" | "fold";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface ShowMeTheCoinPlayer {
  readonly playerId: ShowMeTheCoinPlayerId;
  readonly label: string;
}

export type CoinInventory = Readonly<Record<CoinDenomination, number>>;

/** A player's submitted coins for the current round (list of denominations). */
export interface CoinSubmission {
  readonly coins: readonly number[];
}

/** Public result of a settled round (revealed values are public). */
export interface RoundResult {
  readonly round: number;
  readonly leadPlayerId: ShowMeTheCoinPlayerId;
  readonly counts: Readonly<Record<ShowMeTheCoinPlayerId, number>>;
  readonly values: Readonly<Record<ShowMeTheCoinPlayerId, number>>;
  /** The player who folded, if any (no showdown). */
  readonly folderId: ShowMeTheCoinPlayerId | null;
  /** Round winner, or "push" on equal value. */
  readonly winnerId: ShowMeTheCoinPlayerId | "push";
  readonly pot: number;
}

export interface SubmitCoinsAction {
  readonly type: "SubmitCoins";
  readonly playerId: ShowMeTheCoinPlayerId;
  /** Denominations submitted (each ∈ COIN_DENOMINATIONS). */
  readonly coins: readonly number[];
}

export interface BetAction {
  readonly type: "Bet";
  readonly playerId: ShowMeTheCoinPlayerId;
  readonly move: BetMove;
}

export type ShowMeTheCoinAction = SubmitCoinsAction | BetAction;

export interface ShowMeTheCoinState {
  readonly gameId: string;
  readonly players: readonly ShowMeTheCoinPlayer[];
  readonly phase: ShowMeTheCoinPhase;
  readonly round: number;
  readonly leadPlayerId: ShowMeTheCoinPlayerId;
  readonly currentPlayerId: ShowMeTheCoinPlayerId;
  /** Remaining coins per player (server-authoritative; private per owner). */
  readonly inventory: Readonly<Record<ShowMeTheCoinPlayerId, CoinInventory>>;
  readonly chips: Readonly<Record<ShowMeTheCoinPlayerId, number>>;
  /** This round's submission per player; null until submitted. Value private. */
  readonly submission: Readonly<
    Record<ShowMeTheCoinPlayerId, CoinSubmission | null>
  >;
  readonly pot: number;
  /** Chips contributed to the pot this round per player (incl. ante). */
  readonly contributed: Readonly<Record<ShowMeTheCoinPlayerId, number>>;
  readonly raisesUsed: number;
  /** Who made the last bet/raise this street; null before any aggression. */
  readonly lastAggressorId: ShowMeTheCoinPlayerId | null;
  /** Last settled round (public reveal) — drives the showdown UI. */
  readonly lastRound: RoundResult | null;
  readonly winnerId: ShowMeTheCoinPlayerId | null;
  readonly lastAction: ShowMeTheCoinAction | null;
  readonly moveNumber: number;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly firstLeadPlayerId?: ShowMeTheCoinPlayerId;
}

// Viewer projection: opponent inventory is hidden (null); opponent's current
// submission shows only its count, not the denominations (value masked).
export interface ViewerSubmission {
  readonly count: number;
  /** Denominations — null when masked (opponent's un-revealed submission). */
  readonly coins: readonly number[] | null;
}

export interface ShowMeTheCoinViewerState
  extends Omit<ShowMeTheCoinState, "inventory" | "submission"> {
  readonly inventory: Readonly<
    Record<ShowMeTheCoinPlayerId, CoinInventory | null>
  >;
  readonly submission: Readonly<
    Record<ShowMeTheCoinPlayerId, ViewerSubmission | null>
  >;
}
